import { queryOptions, useQuery } from '@tanstack/react-query';

import { apiClient } from '@cast/infrastructure-api';

type UseVulnerabilitiesTrendQueryParams = {
  clusterIds?: string[];
  labels?: string[];
  namespaces?: string[];
  startTime?: string;
  endTime?: string;
  stepSeconds?: number;
  limit?: number;
  enabled?: boolean;
};

const SECURITY_VULNERABILITIES_TREND_QUERY_KEY =
  'security-vulnerabilities-trend';

const getVulnerabilitiesTrendQueryKey = ({
  clusterIds,
  labels,
  namespaces,
  startTime,
  endTime,
  stepSeconds,
  limit,
}: Omit<UseVulnerabilitiesTrendQueryParams, 'enabled'>) =>
  [
    SECURITY_VULNERABILITIES_TREND_QUERY_KEY,
    clusterIds,
    labels,
    namespaces,
    startTime,
    endTime,
    stepSeconds,
    limit,
  ] as const;

const getVulnerabilitiesTrendQueryOptions = ({
  clusterIds,
  labels,
  namespaces,
  startTime,
  endTime,
  stepSeconds,
  limit,
  enabled = true,
}: UseVulnerabilitiesTrendQueryParams) =>
  queryOptions({
    queryKey: getVulnerabilitiesTrendQueryKey({
      clusterIds,
      labels,
      namespaces,
      startTime,
      endTime,
      stepSeconds,
      limit,
    }),
    queryFn: async () => {
      const { data } = await apiClient.securityInsights.getVulnerabilitiesTrend(
        {
          clusterIds,
          labels,
          namespaces,
          startTime,
          endTime,
          stepSeconds,
          limit,
        }
      );
      return data;
    },
    enabled: enabled && !!startTime && !!endTime,
  });

export const useVulnerabilitiesTrendQuery = (
  params: UseVulnerabilitiesTrendQueryParams = {}
) => {
  const {
    isLoading,
    isFetching,
    data: trendData,
    error,
    refetch,
  } = useQuery(getVulnerabilitiesTrendQueryOptions(params));

  return { isLoading, isFetching, trendData, error, refetch };
};
